// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The three shapes a run can take. A mode must change how the game PLAYS, not a number — and the only
// test of that which means anything is **what the thumb does**:
//
//   ascent   → build + dispatch   : capital sequencing. What you buy at the end of peak one is the game.
//   refit    → triage + dispatch  : money cannot buy space. The levers are controller-side.
//   turnover → dispatch only      : no build verb at all. Six minutes, and it proves the lift layer
//                                   is a real mechanic rather than decoration.
//
// Every run in every mode OPENS MID-CRISIS. There is no empty lot in this game. A management sim
// whose first minute is an empty grid and a build menu is the exact shape that gets admired and not
// played, and the fix is to hand the player a tower that is already going wrong.
//
// `modeOf()` validates any id that arrives from outside (a URL, a share code, localStorage) with
// `Object.hasOwn` rather than `MODES[id] ||` — a stored value of "constructor" is a real key on a
// plain object and would otherwise reach the generator as a function.

import type { RiserPolicy } from './tower';

export type ModeId = 'ascent' | 'refit' | 'turnover';

export interface Opening {
  /** Storeys that exist at the start (the shell the player inherits). */
  storeys: number;
  /** Roughly how many of the buildable slots are already let. 1 = full. */
  letFraction: number;
  /** Satisfaction every inherited tenancy starts on. */
  startSat: number;
  /** Risers as [lowStorey, highStorey, column, policy, expressFrom]. */
  risers: ReadonlyArray<readonly [number, number, number, RiserPolicy?, number?]>;
  /** Cars as [riserIndex, express]. */
  cars: ReadonlyArray<readonly [number, boolean]>;
  /**
   * The left column of a two-wide service void the original developer left clear, or -1 for none.
   *
   * Ascent has one and the full towers do not, and that is the whole difference between "you may cut
   * a second riser" and "you must demolish tenants to cut a second riser". Without it, an opening
   * that fills every column makes the only fix to a strangled tower PHYSICALLY IMPOSSIBLE by day
   * four, at any price — measured, on every seed, at the same population.
   */
  voidCol: number;
}

export interface Goal {
  rating?: number;
  meanSat?: number;
  pop?: number;
  /** Population as a fraction of what the run started with (Refit: you must not shed the tower). */
  popFraction?: number;
}

export interface Mode {
  id: ModeId;
  name: string;
  blurb: string;
  /** One line for the help card, phrased as what the mode does TO YOU. */
  rule: string;
  days: number;
  cash: number;
  /** A standing daily charge — Refit inherits a debt on the refurbishment. */
  dailyCharge: number;
  opening: Opening;
  /** Whether the build tray exists at all. */
  build: boolean;
  /** The highest storey the player may build on. */
  maxStorey: number;
  /** Ticks of continuous waiting before a person walks out. Refit's tenants are captive and endure more. */
  giveUpTicks: number;
  goal: Goal;
  /** What the results screen leads with. */
  scoreLabel: string;
}

export const MODES: Record<ModeId, Mode> = {
  ascent: {
    id: 'ascent',
    name: 'Ascent',
    blurb: 'Six storeys, one car, and a peak already running. Grow it without strangling it.',
    rule: 'Every riser you cut eats two columns on every storey it passes — the lift costs you the floors.',
    days: 10,
    cash: 2400,
    dailyCharge: 0,
    opening: {
      storeys: 6,
      letFraction: 0.85,
      startSat: 0.62,
      risers: [[0, 8, 5]],
      cars: [[0, false]],
      voidCol: 9,
    },
    build: true,
    maxStorey: 23,
    giveUpTicks: 1500,
    goal: { rating: 750, pop: 55 },
    scoreLabel: 'Rating',
  },

  refit: {
    id: 'refit',
    name: 'Refit',
    blurb: 'A full 18-storey tower whose middle storeys no lift stops at, and tenants already going.',
    rule: 'The tower is full: you cannot build your way out, only re-cut, re-let and dispatch your way out.',
    days: 5,
    cash: 2400,
    dailyCharge: 470,
    opening: {
      storeys: 18,
      letFraction: 1,
      startSat: 0.36,
      // The diagnosis the mode is about: riser A was set to run express from storey 10 and riser B
      // was never taken past storey 9, so the whole middle of the building is served by exactly one
      // car and the top by exactly one. Nothing is unreachable — everything is under-served, which is
      // a far harder thing to see and the only thing worth asking a player to find.
      risers: [
        [0, 17, 3, 'express', 14],
        [0, 9, 8],
      ],
      cars: [
        [0, false],
        [1, false],
      ],
      voidCol: -1,
    },
    build: true,
    maxStorey: 18,
    giveUpTicks: 1900,
    goal: { rating: 780, pop: 110 },
    scoreLabel: 'Rating',
  },

  turnover: {
    id: 'turnover',
    name: 'Turnover',
    blurb: 'A full tower, misconfigured. You cannot build a thing — you can only run the lifts better.',
    rule: 'No building, no money: open the risers up and the tower fills — with more people than two cars can carry.',
    days: 6,
    cash: 0,
    dailyCharge: 0,
    opening: {
      storeys: 18,
      letFraction: 0.8,
      startSat: 0.6,
      // Both risers were left running express, so the bottom third of the building is served by
      // nothing at all and stands empty. Opening them back up is the whole puzzle, and it costs the
      // express speed the upper storeys currently enjoy.
      risers: [
        [0, 17, 3, 'express', 12],
        [0, 17, 8, 'express', 9],
      ],
      cars: [
        [0, false],
        [1, false],
      ],
      voidCol: -1,
    },
    build: false,
    maxStorey: 0,
    giveUpTicks: 1500,
    goal: { rating: 560, pop: 70 },
    scoreLabel: 'Rating',
  },
};

export const MODE_IDS: readonly ModeId[] = ['ascent', 'refit', 'turnover'];

export function modeOf(id: string | null | undefined): Mode {
  if (id && Object.hasOwn(MODES, id)) return MODES[id as ModeId];
  return MODES.ascent;
}
